import './jobCardStyles.css'
import { Button } from '@atd/rui-base.mui.button'
import { Card } from '@atd/rui-base.mui.card'
import { CardContent } from '@atd/rui-base.mui.card-content'
import { CardHeader } from '@atd/rui-base.mui.card-header'
import { Collapse } from '@atd/rui-base.mui.collapse'
import { FormControl } from '@atd/rui-base.mui.form-control'
import { IconButton } from '@atd/rui-base.mui.icon-button'
import { InputLabel } from '@atd/rui-base.mui.input-label'
import { Select } from '@atd/rui-base.mui.select'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { errorCatch, errorCheck } from '../lib/actionErrorHandler'

import CloseIcon from '@mui/icons-material/AddBox'
import OpenIcon from '@mui/icons-material/IndeterminateCheckBox'
import { addDays } from 'date-fns'
import { areaMenuItems } from '../commonComponents/unitMenuItems'
import axios from '../lib/axios'
import { convertToDisplay } from '../lib/ddiUnits'
import { useSelector } from 'react-redux'

const JobListSummary = ({name = '', guid = ''}) => {

    const workorderStore = useSelector(store => store.workorder)
    const systemStore = useSelector(store => store.system)

    const {filteredJobs: jobs = [], jobListFilter: filters} = workorderStore
    const systemIds = []

    for (const job of jobs) {
        for (const participant of job.participants) {
            systemIds.push(participant.systemId)
        }
    }

    const {allSystems: systemList = []} = systemStore
    const systems = systemList.filter(s => systemIds.includes(s.id))

    const [areaLabel, setAreaLabel] = useState('ac')
    const [loading, setLoading] = useState(false)
    const [jobRads, setJobRads] = useState([])
    const [growersExpanded, setGrowersExpanded] = useState(true)
    const [systemsExpanded, setSystemsExpanded] = useState(true)

    const abortControllerRef = useRef(null)

    const getJobRads = useCallback(async() => {
        // Abort the previous request if it exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort()
        }

        // Create a new abort controller
        abortControllerRef.current = new AbortController()
        const { signal } = abortControllerRef.current

        // Get SystemId from "System"(hardwareId)
        setLoading(true)
        let systemIds = []
        let partStatus
        let jobArchived
        let startDate = filters.startDate
        let endDate = filters.endDate

        if (filters.selectedSystems && filters.selectedSystems.length > 0) {
            systemIds = filters.selectedSystems.map(systemId => {
                return systems.find(system => system.id == systemId).id
            })
        }

        if (!startDate) {
            startDate = new Date(0)
        }
        if (!endDate) {
            endDate = addDays(new Date(), 1)
        }

        switch (filters.selectedParticipantActivity) {
            case 'joined':
                partStatus = 'InProgress'
                break
            case 'left':
                partStatus = 'Inactive'
                break
            default:
                partStatus = null
        }

        switch (filters.selectedAvailability) {
            case 'archived':
                jobArchived = true
                break
            case 'not archived':
                jobArchived = false
                break
            default:
                jobArchived = null

        }

        try {
            const response = await axios.post('/jobs/net-api/jobs/summary', {
                farmIds: filters.selectedFarms,
                fieldIds: filters.selectedFields,
                growerIds: filters.selectedGrowers,
                systemIds: systemIds,
                jobGuids: jobs.map(j => j.guid),
                startDate: startDate,
                endDate: endDate,
                participantStatus: partStatus,
                jobArchived: jobArchived,
            }, {
                signal,
            })

            const rads = response.data

            // Since we're iterating the current workorderlist; in the event we receive
            // a job from .Net that was not in our current job-list it will be skipped
            // When the job list updates (on it's interval) and the report is re-ran
            // it will be accurate
            setLoading(false)
            setJobRads(rads.jobs || [])
        }
        catch(e) {
            if (e.name === 'AbortError') {
                console.log('Request aborted', e.message)
            }
            else {
                errorCheck(e.response)
                errorCatch(e)
            }
        }
    })

    const generateCSV = () => {
        let csv = 'JobGuid,JobName,JobUsedRxMap,JobTotalArea,JobTotalAreaUnit,JobArchived,ParticipantSystemId,ParticipantSystemName,ParticipantStatus,ParticipantTotalArea,AreaUnit\n'
        for (const rad of jobRads) {
            for (const part of rad.participants) {
                csv += [
                    rad.jobGuid,
                    jobs.find(j => j.guid == rad.jobGuid).name,
                    rad.rxMapUsed,
                    convertToDisplay(rad.totalArea, areaLabel).toFixed(2),
                    areaLabel,
                    rad.jobArchived,
                    part.systemID,
                    part.systemName,
                    part.status,
                    convertToDisplay(part.area, areaLabel).toFixed(2),
                    areaLabel + '\n',
                ].join(',')
            }
        }

        let { startDate, endDate } = filters
        if (!startDate) {
            startDate = new Date(0)
        }
        if (!endDate) {
            endDate = addDays(new Date(), 1)
        }

        const download = document.createElement('a')
        const file = new Blob([csv], {type: 'text/plain'})
        download.href = URL.createObjectURL(file)
        download.download = `jobs${startDate.toISOString().substring(0, 10)}-${endDate.toISOString().substring(0, 10)}.csv`
        download.click()
    }

    const renderOverall = () => {
        let totalArea = 0
        let rxArea = 0
        let straightArea = 0

        for (const job of jobs) {
            const rad = jobRads.find(r => r.jobGuid == job.guid)
            if (!rad) { continue }

            totalArea += rad.totalArea
            if (rad.rxMapUsed) {
                rxArea += rad.totalArea
            }
            else {
                straightArea += rad.totalArea
            }
        }

        return (
            <table className="summaryCardTable">
                <tbody>
                    <tr style={{backgroundColor: 'var(--foreground-light)'}} key={`overall-total`}>
                        <td className="leftEdge">{`Total Area`}</td>
                        <td/>
                        <td className="rightEdge">{ `${convertToDisplay(totalArea, areaLabel).toFixed(2)} ${areaLabel}`}</td>
                    </tr>
                    <tr key={`overall-rx`}>
                        <td> </td>
                        <td>{`Rx Map`}</td>
                        <td className="rightEdge">{ `${convertToDisplay(rxArea, areaLabel).toFixed(2)} ${areaLabel}`}</td>
                    </tr>
                    <tr key={`$overall-straightRate`}>
                        <td> </td>
                        <td>{`Non-Rx`}</td>
                        <td className="rightEdge">{ `${convertToDisplay(straightArea, areaLabel).toFixed(2)} ${areaLabel}`}</td>
                    </tr>
                </tbody>
            </table>
        )
    }

    const renderGrowerLine = (grower) => {
        let totalArea = 0
        let rxArea = 0
        let straightArea = 0
        for (const job of jobs) {
            const rad = jobRads.find(r => r.jobGuid == job.guid)
            if (!rad) { continue }

            if (job.growerId === grower?.id) {
                totalArea += rad.totalArea
                if (rad.rxMapUsed) {
                    rxArea += rad.totalArea
                }
                else {
                    straightArea += rad.totalArea
                }
            }
        }

        return ([
            <tr style={{backgroundColor: 'var(--foreground-light)'}} key={`${grower.id}-total`}>
                <td className="leftEdge">{grower.name || 'N/A'}</td>
                <td/>
                <td className="rightEdge">{ `${convertToDisplay(totalArea, areaLabel).toFixed(2)} ${areaLabel}`}</td>
            </tr>,
            <tr key={`${grower.id}-rx`}>
                <td> </td>
                <td>{`Rx Map`}</td>
                <td className="rightEdge">{ `${convertToDisplay(rxArea, areaLabel).toFixed(2)} ${areaLabel}`}</td>
            </tr>,
            <tr key={`${grower.id}-straightRate`}>
                <td> </td>
                <td>{`Non-Rx`}</td>
                <td className="rightEdge">{ `${convertToDisplay(straightArea, areaLabel).toFixed(2)} ${areaLabel}`}</td>
            </tr>,
        ])
    }

    const renderGrowers = () => {
        let growersJSX
        const growerD = {}
        for (const j of jobs) {
            const rad = jobRads.find(r => r.jobGuid == j.guid)
            if (rad && rad.growerId) {
                growerD[rad.growerId] = {id: rad.growerId, name: rad.growerName}
            }
        }

        const growersWithJobs = Object.values(growerD)

        if (growersWithJobs.length == 0) {
            growersJSX = <span>No Growers</span>
        }
        else {
            growersJSX = (
                <table className="summaryCardTable" >
                    <tbody>
                        {growersWithJobs.map(renderGrowerLine)}
                    </tbody>
                </table>
            )
        }

        const isExpandable = !!growersWithJobs.length
        const toggle = () => setGrowersExpanded(v => !v)

        return (
            <Card sx={{marginBottom: '5px'}}>
                <CardHeader
                    sx={{
                        backgroundColor: isExpandable ? '#666666' : '#EEEEEE',
                        cursor: isExpandable ? 'pointer' : 'default',
                    }}
                    titleTypographyProps={{style: {fontSize: '15px', color: isExpandable ? 'white' : 'black'}}}
                    title="Area By Grower"
                    onClick={isExpandable ? toggle : undefined}
                    action={isExpandable ? (
                        <IconButton onClick={(e) => { e.stopPropagation(); toggle() }} size="small">
                            {growersExpanded ? <OpenIcon htmlColor="white"/> : <CloseIcon htmlColor="white"/>}
                        </IconButton>
                    ) : undefined}
                />
                <Collapse in={isExpandable && growersExpanded}>
                    <CardContent sx={{paddingLeft: 0, paddingRight: 0}}>
                        {growersJSX}
                    </CardContent>
                </Collapse>
            </Card>
        )
    }

    const renderSystemLine = (system, i) => {
        let totalArea = 0
        let rxArea = 0
        let straightArea = 0
        for (const job of jobs) {
            const rad = jobRads.find(r => r.jobGuid == job.guid)
            if (!rad || !rad.participants) { continue }

            for (const participant of rad.participants) {
                if (participant.systemID == system.id) {
                    totalArea += participant.area
                    if (rad.rxMapUsed) {
                        rxArea += participant.area
                    }
                    else {
                        straightArea += participant.area
                    }
                }
            }
        }

        return ([
            <tr style={{backgroundColor: 'var(--foreground-light)'}} key={`${system.id}-total`}>
                <td className="leftEdge">{system.name || system.id || 'N/A'}</td>
                <td/>
                <td className="rightEdge">{ `${convertToDisplay(totalArea, areaLabel).toFixed(2)} ${areaLabel}`}</td>
            </tr>,
            <tr key={`${system.id}-rx`}>
                <td> </td>
                <td>{`Rx Map`}</td>
                <td className="rightEdge">{ `${convertToDisplay(rxArea, areaLabel).toFixed(2)} ${areaLabel}`}</td>
            </tr>,
            <tr key={`${system.id}-straightRate`} style={{padding: '2px'}}>
                <td> </td>
                <td>{`Non-Rx`}</td>
                <td className="rightEdge">{ `${convertToDisplay(straightArea, areaLabel).toFixed(2)} ${areaLabel}`}</td>
            </tr>,
        ])
    }

    const renderSystems = () => {
        let systemsJSX

        const jobParticipants = {}
        for (const job of jobs) {
            const rad = jobRads.find(r => r.jobGuid == job.guid)
            if (!rad) { continue }

            for (const part of rad.participants) {
                jobParticipants[part.systemID] = {
                    id: part.systemID,
                    name: part.systemName,
                }
            }
        }
        const participants = Object.values(jobParticipants)

        if (!participants.length) {
            systemsJSX = <span>No Systems</span>
        }
        else {
            systemsJSX = (
                <table className="summaryCardTable">
                    <tbody>
                        {
                            participants.map(renderSystemLine)
                        }
                    </tbody>
                </table>
            )
        }

        const isExpandable = !!systems.length
        const toggle = () => setSystemsExpanded(v => !v)

        return (
            <Card sx={{marginBottom: '5px'}}>
                <CardHeader
                    sx={{
                        backgroundColor: isExpandable ? '#666666' : '#EEEEEE',
                        cursor: isExpandable ? 'pointer' : 'default',
                    }}
                    titleTypographyProps={{style: {fontSize: '15px', color: isExpandable ? 'white' : 'black'}}}
                    title="Area By System"
                    onClick={isExpandable ? toggle : undefined}
                    action={isExpandable ? (
                        <IconButton onClick={(e) => { e.stopPropagation(); toggle() }} size="small">
                            {systemsExpanded ? <OpenIcon htmlColor="white"/> : <CloseIcon htmlColor="white"/>}
                        </IconButton>
                    ) : undefined}
                />
                <Collapse in={isExpandable && systemsExpanded}>
                    <CardContent sx={{paddingLeft: 0, paddingRight: 0}}>
                        {systemsJSX}
                    </CardContent>
                </Collapse>
            </Card>
        )
    }

    useEffect(() => {
        getJobRads()

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [jobs, filters])

    return (
        <div name="jobSummaryArea" style={{width: '100%', maxWidth: '800px'}}>
            <h2 id="loading" style={{display: loading ? 'inherit' : 'none', color: 'red'}}>Loading Job Files...</h2>
            <FormControl variant="standard" fullWidth>
                <InputLabel>Area Units</InputLabel>
                <Select
                    value={areaLabel}
                    onChange={(event) => setAreaLabel(event.target.value)}
                    label="Area Units"
                    name="areaDisplayUnit"
                >
                    {areaMenuItems}
                </Select>
            </FormControl>
            {renderOverall()}
            {renderGrowers()}
            {renderSystems()}
            <br/>
            <Button variant="contained"
                disabled={loading || !jobRads.length }
                color="primary"
                onClick={generateCSV}>Download CSV</Button>
        </div>
    )
}

export default JobListSummary
